import { useQuery } from '@tanstack/react-query';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { dataPlatformClient, sdClient } from 'contexts/SDKClientContext';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import {
    DataPlatformIndicatorFilter,
    getIndicatorSpecs,
} from 'custom_hooks/data_hooks/data_source/dataPlatformIndicatorDataSource';
import { ensureIndicatorFilters } from 'custom_hooks/data_hooks/indicator/ensureIndicatorFilters';
import {
    GeneralIndicatorInfo,
    mapDataPlatformIndicatorToGeneralIndicatorInfo,
    mapPromIndicatorToGeneralIndicatorInfo,
} from 'custom_hooks/data_hooks/indicator/shared';
import { MaxIndicatorSelections } from 'pages/workbench/side_panel/shared';
import { IndicatorInfo } from 'pages/workbench/types';
import { useMemo } from 'react';
import { excludeNullAndUndefined, toMap } from 'utils/array';
import { queryClient, resultsOf } from 'utils/query';
import { buildOrderBy, Filter } from 'utils/query_v3';

const queryKey = 'trend-indicators';

export function invalidateTrendIndicators() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

export const indicatorDefaultSort: DataSourceSort[] = [
    {
        field: 'SystemSortOrder ',
        dir: 'asc',
    },
    {
        field: 'Id',
        dir: 'asc',
    },
];

export type UseTrendIndicatorsOptions = Readonly<{
    indicatorInfo: IndicatorInfo[];
}> &
    DataPlatformIndicatorFilter;

export type TrendIndicator = Readonly<{
    indicatorId: string;
    indicator: GeneralIndicatorInfo;
    color: string;
}>;

export function useTrendIndicators({
    indicatorInfo,
    groupIds,
    customFilterIds,
    indicatorTypes,
    sensorRoleTypes,
    orientations,
    filtered,
}: UseTrendIndicatorsOptions) {
    const { value: timeSeriesCutoverPhase2, loading: isLoadingFeatureFlag } =
        useFeatureFlag('timeSeriesCutoverPhase2');

    const sortExpr = buildOrderBy(indicatorDefaultSort);
    const colorLookup = useMemo(
        () =>
            toMap(
                indicatorInfo,
                (info) => info.indicatorId,
                (info) => info.color,
            ),
        [indicatorInfo],
    );

    const indicatorIds = useMemo(
        () => indicatorInfo.map((info) => info.indicatorId),
        [indicatorInfo],
    );

    const filter = useMemo(
        () =>
            Filter.and(
                sensorRoleTypes &&
                    // Prometheus does not need to be able to handle the filtering by null, as that only exists
                    // for timeseries cutover, so we can safely filter out nulls here.
                    Filter.in('MeasureOf', excludeNullAndUndefined(sensorRoleTypes)),
                Filter.in('Id', indicatorIds),
            ),
        [indicatorIds, sensorRoleTypes],
    );

    return useQuery({
        queryKey: [
            queryKey,
            groupIds,
            filter,
            timeSeriesCutoverPhase2,
            customFilterIds,
            indicatorTypes,
            sensorRoleTypes,
            orientations,
            filtered,
        ],

        queryFn: async () => {
            if (indicatorIds.length === 0 || isLoadingFeatureFlag) {
                return [];
            }

            if (timeSeriesCutoverPhase2) {
                const defaultFilters = await ensureIndicatorFilters();

                const indicators = await dataPlatformClient.indicators.getIndicators({
                    indicatorIds,
                    indicatorSpecs: getIndicatorSpecs(
                        {
                            customFilterIds,
                            sensorRoleTypes,
                            indicatorTypes,
                            orientations,
                            filtered,
                        },
                        defaultFilters,
                    ),
                    limit: MaxIndicatorSelections,
                });

                return indicators.data.map<TrendIndicator>((indicator) => {
                    const color = colorLookup.get(indicator.id);
                    if (!color) {
                        throw new Error('Missing color');
                    }

                    return {
                        indicatorId: indicator.id,
                        indicator:
                            mapDataPlatformIndicatorToGeneralIndicatorInfo(indicator),
                        color,
                    };
                });
            }

            const indicators = await resultsOf(
                sdClient.getIndicators({
                    groupIds,
                    filterIds: customFilterIds,
                    indicatorTypeIds: indicatorTypes,
                    filter,
                    page: 1,
                    pageLimit: MaxIndicatorSelections,
                    orderBy: sortExpr,
                }),
            );

            return indicators.map<TrendIndicator>((indicator) => {
                const color = colorLookup.get(indicator.Id);
                if (!color) {
                    throw new Error('Missing color');
                }

                return {
                    indicatorId: indicator.Id,
                    indicator: mapPromIndicatorToGeneralIndicatorInfo(indicator),
                    color,
                };
            });
        },

        placeholderData: (previousData: TrendIndicator[] | undefined, previousQuery) =>
            // If the previous query was for a different cutover phase, we don't want to use its data as placeholder data.
            previousQuery?.queryKey[3] !== timeSeriesCutoverPhase2
                ? undefined
                : previousData,

        enabled: !isLoadingFeatureFlag && indicatorIds.length > 0,
    });
}
